import {
  Phrase,
  PhraseType,
} from "../../../api/phrasebook";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect } from "react";
import PhrasesManager from "./phrases-manager";
import usePhrasebookStore from "./store";
import {
  BatchAdvancedStats,
  BrandBatchSettings,
} from "../../../pages/playground/types";
import { getEnabledGroups } from "../../../utils/content-groups";
import EditorNotes from "../notes";
import { processPhrasesValidation } from "../limits-processor";
import { Save, Search } from "@mui/icons-material";
import { useAuthStore } from "../../../authProvider";

export default function EditorPhrasebookEditor({
  jobId,
  batchId,
  jobPhrases,
  settings,
  wordsToAvoid,
  updatePhrasesByType,
  handleSavePhrases,
  advancedStats,
  readOnly,
  hasBulletPoints,
  isAIEnabled,
}: {
  jobId: string;
  batchId: string;
  jobPhrases: Phrase[];
  setJobPhrases: (phrases: Phrase[]) => void;
  settings: BrandBatchSettings;
  wordsToAvoid?: string[];
  updatePhrasesByType: (type: PhraseType, phrases: Phrase[]) => void;
  handleSavePhrases: () => void;
  advancedStats: BatchAdvancedStats | null;
  readOnly?: boolean;
  hasBulletPoints?: boolean;
  isAIEnabled?: boolean;
}) {
  const { user } = useAuthStore();

  const {
    setJobId,
    setBatchId,
    setActiveField,
    setHasChanges,
    savingPhrases,
    hasChanges,
    startKeywordSearch,
  } = usePhrasebookStore();

  useEffect(() => {
    setJobId(jobId);
    setBatchId(batchId);
    setHasChanges(false);
    setActiveField(null);
  }, [jobId, batchId, setJobId, setBatchId, setHasChanges, setActiveField]);

  const processedLimits = processPhrasesValidation(
    jobPhrases,
    undefined,
    wordsToAvoid
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <EditorNotes jobId={jobId} />

      <Box
        sx={{
          display: "flex",
          gap: "1rem",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: user?.isClientRelated ? "none" : "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.9rem",
                color: "text-main",
              }}
            >
              <b>Batch stats: </b>
              {advancedStats?.average_words_per_job} words/sku,{" "}
              {advancedStats?.translation_metrics.word_reuse_percentage}% reuse.
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "0.9rem",
                color: "text-main",
              }}
            >
              <b>Job stats: </b>
              {processedLimits.phrasesCount} phrases,{" "}
              {processedLimits.charactersCount} characters,{" "}
              {processedLimits.wordsCount} words.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "0.5rem",
            width: "100%",
            flexDirection: user?.isClientRelated ? "row" : "column",
            justifyContent: user?.isClientRelated
              ? "space-between"
              : "flex-end",
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<Search />}
            onClick={() => {
              startKeywordSearch();
            }}
          >
            Keywords Search
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={
              savingPhrases ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Save />
              )
            }
            onClick={() => handleSavePhrases()}
            disabled={savingPhrases || !jobId || !hasChanges}
          >
            Save
          </Button>
        </Box>
      </Box>

      {processedLimits.forbiddenWords.length > 0 && (
        <Box>
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "text-main",
            }}
          >
            <b style={{ color: "red" }}>
              {" "}
              Forbidden words: {processedLimits.forbiddenWords.join(", ")}
            </b>
          </Typography>
        </Box>
      )}

      {getEnabledGroups(settings).map((group) => (
        <PhrasesManager
          key={group.id}
          phrases={jobPhrases.filter(
            (phrase) => phrase && phrase?.type === group.id
          )}
          updatePhrases={(phrases) => {
            updatePhrasesByType(group.id as PhraseType, phrases);
          }}
          type={group.id as PhraseType}
          batchSettings={settings}
          contentGroup={group}
          wordsToAvoid={wordsToAvoid}
          readOnly={readOnly}
          hasBulletPoints={hasBulletPoints}
          isAIEnabled={isAIEnabled}
        />
      ))}
    </Box>
  );
}
